/**
 * Showing a stored timestamp in campus time, and "time ago" (docs/DUPLICATION_AUDIT.md,
 * B13).
 *
 * Two kinds of stored value, and the label must say which a caller has:
 *  - UTC: event_datetime_utc, EventDateTime (active alerts), create_time, server
 *    last_heard / checkin_time.
 *  - Campus-local wall clock: *_local fields, device last_active / last_alarm, the
 *    residents list's check_in_time.
 * tz_offset is signed hours from UTC (getCampusTzOffset in services/session.js).
 */

import { DateTime } from 'luxon';
import { convertIsoUTCToLocal, campusLocalToMs } from '../libs.jsx';
import { utcToMs } from './activeAlerts.js';

const DASH = '—';

/**
 * A UTC timestamp in campus time, in the app's format ("10/01/2026 4:56PM").
 * Replaces six `formatUtcToLocal` copies that formatted in the viewer's browser
 * timezone, so a viewer outside the campus saw shifted times, and that built
 * "YYYY-MM-DD HH:MM:SSZ", which Safari parses as Invalid Date.
 */
export function formatUtcForCampus(value, tzOffset) {
    if (!value) return DASH;
    return convertIsoUTCToLocal(value, Number(tzOffset) || 0) || DASH;
}

/* A UTC "YYYY-MM-DD HH:MM:SS" shifted to campus wall clock, or null when unset. Parsed
   in the utc zone so a DST change in the viewer's browser cannot move it an hour.
   Legacy rows can hold the zero date, which luxon reads as invalid. */
function utcToCampusDateTime(value, tzOffset) {
    if (!value) return null;
    const dt = DateTime.fromSQL(String(value), { zone: 'utc' });
    return dt.isValid ? dt.plus({ hours: Number(tzOffset) || 0 }) : null;
}

/** A UTC timestamp as a campus-local date ("10/01/2026"), or '' when unset. */
export function utcToCampusDate(value, tzOffset) {
    return utcToCampusDateTime(value, tzOffset)?.toFormat('MM/dd/yyyy') || '';
}

/** A UTC timestamp as a campus-local `<input type="date">` value ("2026-10-01"), or ''. */
export function utcToCampusIsoDate(value, tzOffset) {
    return utcToCampusDateTime(value, tzOffset)?.toFormat('yyyy-MM-dd') || '';
}

/**
 * A campus-local date ("2026-10-01") at `hour` campus time, as the UTC
 * "YYYY-MM-DD HH:MM:SS" the backend stores. 8 AM matches what the resident profile
 * writes, and round-trips through utcToCampusIsoDate to the same date for any offset.
 */
export function campusDateToUtc(isoDate, tzOffset, hour = 8) {
    const dt = DateTime.fromISO(String(isoDate || ''), { zone: 'utc' });
    if (!dt.isValid) return null;
    return dt.set({ hour }).minus({ hours: Number(tzOffset) || 0 }).toFormat('yyyy-MM-dd HH:mm:ss');
}

/** "Just now", "5m ago", "3h ago", "2d ago", or `olderLabel` past 30 days. */
export function formatTimeAgo(thenMs, olderLabel) {
    if (thenMs == null || Number.isNaN(thenMs)) return DASH;
    const diffMs = Date.now() - thenMs;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 30) return `${diffDay}d ago`;
    return olderLabel;
}

/** "Time ago" for a UTC timestamp; past 30 days, the date in the viewer's locale. */
export function relativeTimeFromUtc(value) {
    const ms = utcToMs(value);
    if (ms == null) return DASH;
    return formatTimeAgo(ms, new Date(ms).toLocaleDateString());
}

/**
 * "Time ago" for a campus-local wall-clock value, anchored to the campus offset so it
 * is right for a viewer in any timezone; past 30 days, the campus date (M/d/yyyy).
 */
export function relativeTimeFromCampusLocal(value, tzOffset = 0) {
    if (!value) return DASH;
    const ms = campusLocalToMs(value, tzOffset);
    if (Number.isNaN(ms)) return DASH;
    let wall = DateTime.fromSQL(value, { zone: 'utc' });
    if (!wall.isValid) wall = DateTime.fromISO(value, { zone: 'utc' });
    return formatTimeAgo(ms, wall.isValid ? wall.toFormat('M/d/yyyy') : DASH);
}
